import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
    List, Map as MapIcon, ChevronDown, ChevronUp, Check, 
    MapPin as MapPinIcon, Settings, Truck, Package, ClipboardCheck, 
    FileText, ChevronRight, Loader
} from 'lucide-react';

import GoodsCollectedView from './components/GoodsCollectedView';
import DispatchView from './components/DispatchView';
import FactoryReceivedView from './components/FactoryReceivedView';
import FactoryProcessingView from './components/FactoryProcessingView';
import FactoryProcessedView from './components/FactoryProcessedView';
import RouteMapView from './components/RouteMapView';
import { getTraceByBatchCode, formatDateTime, formatLocation, getPropertyValue } from '../../services/traceability-api';

const STEP_ICONS = [
    <Package size={20} />,
    <Truck size={20} />,
    <ClipboardCheck size={20} />,
    <Settings size={20} />,
    <ClipboardCheck size={20} />,
    <FileText size={20} />
];

const TraceabilityResult = () => {
    const location = useLocation();
    const { t } = useTranslation();
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
    const [traceData, setTraceData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Extract batchId from URL path (handles slashes in batch codes)
    const pathname = location.pathname;
    const encodedBatchId = pathname.replace(/^\/(traceability-result|traceability)\//, '');
    const batchId = (() => {
        try {
            return decodeURIComponent(encodedBatchId).trim();
        } catch (e) {
            return encodedBatchId.trim();
        }
    })();

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth <= 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch trace data from API
    useEffect(() => {
        if (!batchId) return;
        const controller = new AbortController();
        
        setLoading(true);
        setError(null);
        
        getTraceByBatchCode(batchId, { signal: controller.signal })
            .then(data => {
                setTraceData(data);
                setLoading(false);
            })
            .catch(err => {
                if (err.name === 'AbortError') return;
                console.error('Failed to fetch trace data:', err);
                setError(err.message);
                setLoading(false);
            });

        return () => controller.abort();
    }, [batchId]);

    // Build timeline steps from API data
    const allSteps = traceData?.timeline?.map((step, index) => ({
        label: step.label || 'Unknown Step',
        date: formatDateTime(step.startTime || step.endTime),
        location: formatLocation(step.location),
        status: step.status || 'pending',
        icon: STEP_ICONS[index] || <Package size={20} />,
        properties: step.properties || [],
        processType: step.processType,
        startTime: step.startTime,
        endTime: step.endTime
    })) || [];

    // Public traceability shows completed history only.
    const timelineSteps = allSteps.filter(step => step.status === 'completed');
    const maxReachedStepIndex = Math.max(0, timelineSteps.length - 1);

    const [activeStep, setActiveStep] = useState(0);
    const [viewMode, setViewMode] = useState('list');

    // Update active step when data loads
    useEffect(() => {
        if (timelineSteps.length > 0) {
            setActiveStep(maxReachedStepIndex);
        }
    }, [traceData]);

    // Extract header data from API response
    const batch = traceData?.batch;
    const headerData = {
        name: batch ? (getPropertyValue(batch.properties, 'Variety') || getPropertyValue(batch.properties, 'Crop') || batch.name || 'Batch') : batchId,
        batchId: batch?.name || batch?.batchId || batchId,
        status: traceData?.status || '—',
        type: batch ? (getPropertyValue(batch.properties, 'Crop') || '—') : '—',
    };

    const currentProgressCount = traceData?.completedSteps ?? timelineSteps.length;
    const totalSteps = traceData?.totalSteps || allSteps.length || 1;
    const visibleStepCount = timelineSteps.length;
    const currentStep = timelineSteps[activeStep];

    // Get step data for each view component
    const getStepData = (index) => {
        const step = timelineSteps[index];
        const stepProperties = step?.properties || [];
        const batchProperties = traceData?.batch?.properties || [];
        return {
            properties: [...stepProperties, ...batchProperties],
            batch: traceData?.batch,
            childBatches: traceData?.childBatches,
            startTime: step?.startTime,
            endTime: step?.endTime,
        };
    };

    /* ── Loading State ── */
    if (loading) {
        return (
            <div className={`trace-result-wrapper ${isMobile ? 'mobile' : 'desktop'}`}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '16px' }}>
                    <Loader size={32} className="spin-animation" color="#02C170" />
                    <p style={{ color: '#62748E', fontSize: '14px' }}>Loading trace data...</p>
                </div>
            </div>
        );
    }

    /* ── Error State ── */
    if (error || !traceData) {
        return (
            <div className={`trace-result-wrapper ${isMobile ? 'mobile' : 'desktop'}`}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', gap: '16px', padding: '24px' }}>
                    <Package size={48} color="#CAD5E2" />
                    <h3 style={{ color: '#314158', margin: 0 }}>No Trace Data Found</h3>
                    <p style={{ color: '#62748E', fontSize: '14px', textAlign: 'center', maxWidth: '400px' }}>
                        {error || `No traceability data found for batch "${batchId}". The batch may not exist or hasn't been synced yet.`}
                    </p>
                </div>
            </div>
        );
    }

    /* ── VIEW TOGGLE ── */
    const ViewToggle = ({ showLabels = false, isMobileToggle = false }) => (
        <div className={`trace-view-toggle-container ${isMobileToggle ? 'absolute-mobile' : ''}`}>
            <button 
                onClick={() => setViewMode('list')}
                className={`toggle-btn ${viewMode === 'list' ? 'active' : ''} ${showLabels ? 'with-label' : 'icon-only'}`}
            >
                <List size={16} />
                {showLabels && 'Timeline'}
            </button>
            <button 
                onClick={() => setViewMode('map')}
                className={`toggle-btn map-btn ${viewMode === 'map' ? 'active' : ''} ${showLabels ? 'with-label' : 'icon-only'}`}
            >
                <MapIcon size={16} />
                {showLabels && 'Map View'}
            </button>
        </div>
    );

    /* ── Render Step Detail Content ── */
    const renderStepContent = (stepIndex, isMobileView) => {
        const step = timelineSteps[stepIndex];
        if (!step) return null;
        
        const stepData = getStepData(stepIndex);
        
        switch (step.processType) {
            case 'GRN_BATCH_CREATE': return <GoodsCollectedView isMobile={isMobileView} data={stepData} />;
            case 'GRN_BATCH_CLOSE': return <DispatchView isMobile={isMobileView} data={stepData} />;
            case 'FACTORY_RECEIVED': return <FactoryReceivedView isMobile={isMobileView} data={stepData} />;
            case 'FACTORY_PROCESS': return <FactoryProcessingView isMobile={isMobileView} data={stepData} />;
            case 'FACTORY_OUT': return <FactoryProcessedView isMobile={isMobileView} data={stepData} />;
            default: return null;
        }
    };

    /* ══════════════════════════════════════════════════════════════
       DESKTOP TIMELINE: Left sidebar + Right detail panel
       ══════════════════════════════════════════════════════════════ */
    const DesktopTimelineView = () => (
        <div className="trace-desktop-layout">
            {/* LEFT: Vertical Timeline Sidebar */}
            <div className="trace-sidebar">
                {timelineSteps.map((step, index) => {
                    const isCompleted = step.status === 'completed';
                    const isInProgress = step.status === 'in-progress';
                    const isPending = step.status === 'pending';
                    const isActive = activeStep === index;
                    const isLast = index === visibleStepCount - 1;

                    return (
                        <div key={index} className="trace-sidebar-item">
                            {/* Step Indicator + Connecting Line */}
                            <div className="indicator-col">
                                {/* Top line */}
                                {index > 0 && (
                                    <div className="line-top" style={{ 
                                        backgroundColor: isCompleted || isInProgress || isActive ? '#02C170' : '#E2E8F0'
                                    }}></div>
                                )}
                                {/* Icon circle */}
                                <div className={`icon-circle ${isActive ? 'active' : ''}`} style={{ 
                                    backgroundColor: (isActive || isCompleted || isInProgress) ? '#02C170' : '#F1F5F9',
                                    color: (isCompleted || isInProgress || isActive) ? '#FFF' : '#CAD5E2'
                                }}>
                                    {isCompleted ? <Check size={20} strokeWidth={2.5} /> : step.icon}
                                </div>
                                {/* Bottom line */}
                                {!isLast && (
                                    <div className="line-bottom" style={{ 
                                        backgroundColor: isCompleted || isInProgress || isActive ? '#02C170' : '#E2E8F0'
                                    }}></div>
                                )}
                            </div>

                            {/* Step Label Button */}
                            <div 
                                onClick={() => !isPending && setActiveStep(index)}
                                className={`content-col ${isActive ? 'active' : ''}`}
                                style={{ 
                                    cursor: isPending ? 'default' : 'pointer',
                                    opacity: isPending ? 0.5 : 1
                                }}
                            >
                                <div className="text-wrap">
                                    <div className="label" style={{ 
                                        color: isActive ? '#FFF' : isPending ? '#CAD5E2' : '#071422'
                                    }}>
                                        {step.label}
                                    </div>
                                    <div className="loc-row">
                                        <MapPinIcon size={11} color={isActive ? 'rgba(255,255,255,0.5)' : '#90A1B9'} />
                                        <span style={{ color: isActive ? 'rgba(255,255,255,0.5)' : '#90A1B9' }}>
                                            {step.location}
                                        </span>
                                    </div>
                                </div>
                                <div className="date-col">
                                    <div className="date" style={{ 
                                        color: isActive ? '#FFF' : isInProgress ? '#02C170' : isPending ? '#CAD5E2' : '#071422'
                                    }}>
                                        {step.date.split(' • ')[0]}
                                    </div>
                                    <div className="time" style={{ color: isActive ? 'rgba(255,255,255,0.5)' : '#90A1B9' }}>
                                        {step.date.split(' • ')[1]}
                                    </div>
                                </div>
                                {!isPending && isActive && (
                                    <ChevronRight size={14} color="rgba(255,255,255,0.5)" style={{ marginLeft: '4px', flexShrink: 0 }} />
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* RIGHT: Step Detail Panel */}
            <div className="trace-detail-panel">
                {/* Detail Header */}
                <div className="trace-detail-header">
                    <div className="info-group">
                        <div className="icon-box">
                            {currentStep?.icon}
                        </div>
                        <div>
                            <div className="title-row">
                                <span className="name">{currentStep?.label}</span>
                            </div>
                            <div className="loc-row">
                                <MapPinIcon size={12} color="#90A1B9" />
                                <span>{currentStep?.location}</span>
                            </div>
                        </div>
                    </div>
                    <div className="date-group">
                        <div className="date">{currentStep?.date.split(' • ')[0]}</div>
                        <div className="time">{currentStep?.date.split(' • ')[1]}</div>
                    </div>
                </div>

                {/* Detail Content */}
                <div className="">
                    {renderStepContent(activeStep, false)}
                </div>
            </div>
        </div>
    );

    /* ── Desktop Detail Card Component ── */
    const DesktopDetailCard = ({ icon, title, value }) => (
        <div className="trace-detail-card">
            <div className="card-top">
                <div className="icon-wrap">
                    {icon}
                </div>
                <span className="card-title">{title}</span>
            </div>
            <div className="card-value">{value}</div>
        </div>
    );

    /* ══════════════════════════════════════════════════════════════
       RENDER
       ══════════════════════════════════════════════════════════════ */
    return (
        <div className={`trace-result-wrapper ${isMobile ? 'mobile' : 'desktop'}`}>
            {/* Main Content Area */}
            <div className={`trace-container ${isMobile ? 'mobile' : 'desktop'}`}>
                {/* ── HEADER ── */}
                <div className={`trace-header ${isMobile ? 'mobile' : 'desktop'}`}>
                    <div className="trace-batch-info">
                        <span className={`label ${isMobile ? 'mobile' : ''}`}>Batch ID</span>
                        <div className="dot"></div>
                        <span className={`value ${isMobile ? 'mobile' : ''}`}>{headerData.batchId}</span>
                    </div>
                    <div className={`trace-title-row ${isMobile ? 'mobile' : 'desktop'}`}>
                        <h1 className={`title ${isMobile ? 'mobile' : 'desktop'}`}>
                            {headerData.name}
                        </h1>
                        {/* Badges */}
                        <div className={`trace-badge-list ${isMobile ? 'mobile' : ''}`}>
                            <span className={`badge status ${isMobile ? 'mobile' : ''}`}>
                                {headerData.status}
                            </span>
                            {headerData.type !== '—' && (
                                <span className={`badge type ${isMobile ? 'mobile' : ''}`}>
                                    {headerData.type}
                                </span>
                            )}
                        </div>
                    </div>

                    {/* Mobile: View Toggle */}
                    {isMobile && <ViewToggle isMobileToggle={true} />}
                </div>

                {/* ── CONTENT ── */}
                {viewMode === 'list' ? (
                    isMobile ? (
                        /* ── MOBILE LIST VIEW ── */
                        <div className="trace-content-card mobile">
                            {/* Trace History Header */}
                            <div className="trace-card-header mobile">
                                <div className="title-group">
                                    <h3 className="mobile">Trace History</h3>
                                    <p style={{ fontSize: '12px', color: '#62748E' }}>{currentProgressCount} of {totalSteps} completed</p>
                                </div>
                                <div className="trace-progress-info" style={{ flex: 1, justifyContent: 'flex-end' }}>
                                    <div className="progress-bar-bg mobile">
                                        <div className="progress-bar-fill" style={{ width: `${(currentProgressCount / totalSteps) * 100}%` }}></div>
                                    </div>
                                </div>
                            </div>

                            {/* Mobile Timeline */}
                            <div>
                                {[...timelineSteps].reverse().map((step, r_index) => {
                                    const index = visibleStepCount - 1 - r_index;
                                    const isCompleted = step.status === 'completed';
                                    const isActive = activeStep === index;
                                    const isPending = step.status === 'pending';
                                    const isLastRow = r_index === visibleStepCount - 1;

                                    return (
                                        <div key={index} className="trace-mobile-timeline-item">
                                            {/* Left: Indicator + Line */}
                                            <div className="indicator-col">
                                                <div className={`dot ${isCompleted ? 'completed' : isActive ? 'in-progress' : ''}`}>
                                                    {isCompleted && <Check size={12} color="white" strokeWidth={3} />}
                                                </div>
                                                {!isLastRow && (
                                                    <div className={`line ${(isCompleted || isActive) ? 'active' : ''}`}></div>
                                                )}
                                            </div>

                                            {/* Right: Content */}
                                            <div 
                                                className="content-col"
                                                style={{ cursor: isPending ? 'default' : 'pointer' }}
                                                onClick={() => !isPending && setActiveStep(index)}
                                            >
                                                <div className="header-row">
                                                    <div className="title-group">
                                                        <div className="label-row">
                                                            <span className={`label ${isPending ? 'pending' : ''}`}>{step.label}</span>
                                                        </div>
                                                        <div className={`loc-row ${isPending ? 'pending' : ''}`}>
                                                            <MapPinIcon size={12} />
                                                            <span>{step.location}</span>
                                                        </div>
                                                    </div>
                                                    <div className="date-row">
                                                        <div className="date-box">
                                                            <div className={`date ${isPending ? 'pending' : ''}`}>
                                                                {step.date.split(' • ')[0]}
                                                            </div>
                                                            <div className="time">{step.date.split(' • ')[1]}</div>
                                                        </div>
                                                        {!isPending && (
                                                            <div className={`expand-icon ${isActive ? 'active' : ''}`}>
                                                                {isActive ? <ChevronUp size={16} /> : <ChevronDown size={16} style={{ opacity: 0.4 }} />}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Expanded Details */}
                                                {isActive && (
                                                    <div className="trace-mobile-expanded-content animate-fade-in">
                                                        <div>
                                                            {renderStepContent(activeStep, true)}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    ) : (
                        /* ── DESKTOP LIST VIEW ── */
                        <div className="trace-content-card desktop">
                            {/* Desktop Header */}
                            <div className="trace-card-header desktop">
                                <div className="title-group">
                                    <h3 className="desktop">Trace History</h3>
                                    <p className="desktop">Select any completed step to view recorded information</p>
                                </div>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
                                    <div className="trace-progress-info">
                                        <span className="progress-text">Progress</span>
                                        <div className="progress-bar-bg">
                                            <div className="progress-bar-fill" style={{ width: `${(currentProgressCount / totalSteps) * 100}%` }}></div>
                                        </div>
                                        <span className="progress-text">{currentProgressCount}/{totalSteps}</span>
                                    </div>
                                    <ViewToggle showLabels={true} />
                                </div>
                            </div>

                            {/* Two-column layout */}
                            <DesktopTimelineView />
                        </div>
                    )
                ) : (
                    /* ── MAP VIEW ── */
                    <div className={`trace-content-card ${isMobile ? 'mobile' : 'desktop'}`}>
                        {!isMobile && (
                            <div className="trace-card-header desktop">
                                <div className="title-group">
                                    <h3 className="desktop">Route Map</h3>
                                    <p className="desktop">Traced shipment route and recorded locations</p>
                                </div>
                                <ViewToggle showLabels={true} />
                            </div>
                        )}

                        <div className={`trace-map-container ${isMobile ? 'mobile' : 'desktop'}`}>
                            <RouteMapView 
                                activeStep={activeStep} 
                                setActiveStep={setActiveStep} 
                                timelineSteps={timelineSteps}
                                isMobile={isMobile}
                            />
                        </div>
                    </div>
                )}
            </div>
            
            <style dangerouslySetInnerHTML={{ __html: `
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(8px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in {
                    animation: fadeIn 0.25s ease-out;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                .spin-animation {
                    animation: spin 1s linear infinite;
                }
            `}} />
        </div>
    );
};

export default TraceabilityResult;
